/**
 * AmourLinen Style Quiz (v4.8)
 * - Hides itself entirely when ?result= is in URL (persona-result owns that state)
 * - Otherwise manages quiz state (intro / returning / quiz / loading)
 * - Auto-advance on answer click + Go-back button
 * - Scores answers, determines persona with Q7 tiebreaker
 * - Persists result: localStorage + customer metafield (logged-in)
 * - On completion, redirects to result page with ?result=<persona>
 * - v4.0: persona labels localized via section data attributes;
 *   result URL is locale-prefixed by Liquid
 * - v4.1: listens for "amourlinen:start-quiz" (dispatched by the compact
 *   pre-quiz teaser card in Persona Result) to scroll back up here and
 *   start the quiz, instead of duplicating the intro/quiz flow elsewhere.
 * - v4.2: no longer shows the "returning" (Welcome back / View your edit)
 *   state — Persona Result already shows the unlocked result directly from
 *   localStorage on its own, so that interstitial was a redundant extra
 *   click sitting on top of results that were already visible further down
 *   the page. A saved persona now just hides this whole section instead.
 * - v4.3: this whole hero no longer shows by default. The compact teaser
 *   card further down the page (Persona Result's no-result state) is now
 *   the first thing a visitor sees when opening the page.
 * - v4.4: "amourlinen:start-quiz" (the teaser's "Find out" link/photos) now
 *   jumps straight into question 1 instead of showing the intro hero first
 *   — one less step between "Find out" and the actual quiz.
 * - v4.5: startQuiz() now adds "amourlinen-quiz-active" to <body> so
 *   Persona Result's CSS can hide its tote/teaser row (redundant once the
 *   quiz is already underway) while keeping the tier list above it visible.
 * - v4.6: the 3 mood photos next to each question are now answer buttons
 *   too (same R/M/F mapping as the lettered text options). Selecting
 *   either the text or its matching photo highlights both.
 * - v4.7: removed the auto-scroll (scrollIntoView) that used to run when
 *   "Find out" revealed this section — the quiz already sits right where
 *   the visitor is looking (directly below the tier list they were just
 *   reading), so forcing a scroll was an unwanted extra jump.
 * - v4.8: added analytics tracking so completion volume is actually
 *   visible somewhere — fires "quiz_started" when a visitor begins (fresh
 *   start or retake) and "quiz_completed" (with the resulting persona) once
 *   scoring finishes. Sent to GA4 (gtag), Meta Pixel (fbq) and dataLayer,
 *   whichever of those are already present on the page — every call is
 *   guarded so nothing breaks if a given tool isn't installed.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'amourlinen_quiz_result';

  function trackEvent(name, params) {
    var payload = params || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      }
    } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', name, payload);
      }
    } catch (e) {}
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    } catch (e) {}
  }

  class StyleQuiz {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.isLoggedIn = section.dataset.customerLoggedIn === 'true';
      this.customerPersona = section.dataset.customerPersona || '';
      this.resultPageUrl = section.dataset.resultPageUrl || '/pages/summer-essentials';

      this.personas = {
        R: { slug: 'romantic', label: section.dataset.labelRomantic || "You're the Romantic" },
        M: { slug: 'minimalist', label: section.dataset.labelMinimalist || "You're the Minimalist" },
        F: { slug: 'free-spirit', label: section.dataset.labelFreeSpirit || "You're the Free Spirit" }
      };

      this.answers = [];
      this.currentQuestion = 1;

      this.states = {
        intro: section.querySelector('[data-quiz-state="intro"]'),
        returning: section.querySelector('[data-quiz-state="returning"]'),
        quiz: section.querySelector('[data-quiz-state="quiz"]'),
        loading: section.querySelector('[data-quiz-state="loading"]')
      };

      this.questions = section.querySelectorAll('[data-question]');
      this.progressDots = section.querySelectorAll('[data-progress-dot]');
      this.backBtn = section.querySelector('[data-quiz-action="back"]');

      this.init();
    }

    init() {
      const params = new URLSearchParams(window.location.search);
      if (params.has('result')) {
        this.section.style.display = 'none';
        return;
      }

      this.bindEvents();
      this.determineInitialState();

      /* Pre-quiz teaser card further down the page (Persona Result section,
         no-result state) dispatches this when its "Find out" text or image
         is clicked, since it doesn't own any quiz state itself. */
      document.addEventListener('amourlinen:start-quiz', () => {
        this.section.style.display = '';
        this.startQuiz();
      });
    }

    bindEvents() {
      this.section.addEventListener('click', (e) => {
        const action = e.target.closest('[data-quiz-action]');
        if (action) {
          this.handleAction(action.dataset.quizAction);
          return;
        }
        const answer = e.target.closest('[data-answer]');
        if (answer) {
          this.handleAnswer(answer);
        }
      });
    }

    handleAction(action) {
      switch (action) {
        case 'begin':
          this.startQuiz();
          break;
        case 'retake':
          this.clearSavedResult();
          this.startQuiz();
          break;
        case 'view-edit':
          this.goToResult(this.getSavedPersona());
          break;
        case 'back':
          this.goBack();
          break;
      }
    }

    determineInitialState() {
      /* v4.3: this hero no longer appears by default — the compact teaser
         card further down the page (Persona Result's no-result state) is
         now the first thing a visitor sees when they open the page.
         Clicking its "Find out" link dispatches "amourlinen:start-quiz",
         which the listener in init() catches to reveal this section and
         jump straight into question 1 (v4.4). Whether or not a persona is
         already saved, there's nothing to show here until that happens. */
      this.section.style.display = 'none';
    }

    getSavedPersona() {
      if (this.isLoggedIn && this.customerPersona) {
        return this.customerPersona;
      }
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          return data.persona || null;
        }
      } catch (e) {
        return null;
      }
      return null;
    }

    showReturningState(personaSlug) {
      const personaEntry = Object.values(this.personas).find(p => p.slug === personaSlug);
      if (!personaEntry) {
        this.showState('intro');
        return;
      }
      const nameEl = this.section.querySelector('[data-returning-persona-name]');
      if (nameEl) nameEl.textContent = personaEntry.label;
      const viewBtn = this.section.querySelector('[data-quiz-action="view-edit"]');
      if (viewBtn) viewBtn.setAttribute('href', this.resultPageUrl + '?result=' + personaSlug);
      this.showState('returning');
    }

    startQuiz() {
      this.answers = [];
      this.currentQuestion = 1;
      trackEvent('quiz_started', { method: 'style_quiz' });
      /* v4.5: lets Persona Result's CSS hide the redundant "take the quiz"
         tote/teaser row while this is underway (tier list above it stays
         visible). */
      document.body.classList.add('amourlinen-quiz-active');
      this.showState('quiz');
      this.showQuestion(1);
    }

    goBack() {
      if (this.currentQuestion <= 1) return;
      this.showQuestion(this.currentQuestion - 1);
    }

    showState(stateName) {
      Object.entries(this.states).forEach(([name, el]) => {
        if (!el) return;
        if (name === stateName) {
          el.hidden = false;
          el.classList.add('is-active');
          requestAnimationFrame(() => el.classList.add('is-visible'));
        } else {
          el.hidden = true;
          el.classList.remove('is-active', 'is-visible');
        }
      });
    }

    showQuestion(n) {
      this.questions.forEach(q => {
        const qNum = parseInt(q.dataset.question, 10);
        if (qNum === n) {
          q.classList.add('is-active');
          q.hidden = false;
          requestAnimationFrame(() => q.classList.add('is-visible'));
        } else {
          q.classList.remove('is-active', 'is-visible');
          q.hidden = true;
        }
      });

      this.progressDots.forEach((dot, i) => {
        if (i + 1 <= n) {
          dot.classList.add('is-filled');
        } else {
          dot.classList.remove('is-filled');
        }
      });

      this.currentQuestion = n;

      if (this.backBtn) {
        this.backBtn.style.visibility = n > 1 ? 'visible' : 'hidden';
      }
    }

    handleAnswer(answerBtn) {
      const value = answerBtn.dataset.answer;
      const questionEl = answerBtn.closest('[data-question]');
      const questionNum = parseInt(questionEl.dataset.question, 10);

      this.answers[questionNum - 1] = {
        question: questionNum,
        answer: value,
        isTiebreaker: questionEl.dataset.tiebreaker === 'true'
      };

      /* v4.6: a question now has two separate sets of clickable elements for
         the same 3 choices — the lettered text answers and the mood photos.
         Clear selection state everywhere in the question, then re-apply it
         to every element matching the chosen value, so clicking either one
         highlights both. */
      questionEl.querySelectorAll('[data-answer]').forEach(b => b.classList.remove('is-selected'));
      questionEl.querySelectorAll('[data-answer="' + value + '"]').forEach(b => b.classList.add('is-selected'));

      setTimeout(() => {
        if (questionNum < 7) {
          this.showQuestion(questionNum + 1);
        } else {
          this.completeQuiz();
        }
      }, 400);
    }

    completeQuiz() {
      this.showState('loading');
      setTimeout(() => {
        const persona = this.calculatePersona();
        this.saveResult(persona);
        this.goToResult(persona);
      }, 1200);
    }

    calculatePersona() {
      const counts = { R: 0, M: 0, F: 0 };
      let tiebreakerAnswer = null;

      this.answers.forEach(a => {
        if (a && a.answer) counts[a.answer]++;
        if (a && a.isTiebreaker) tiebreakerAnswer = a.answer;
      });

      const max = Math.max(counts.R, counts.M, counts.F);
      const winners = Object.keys(counts).filter(k => counts[k] === max);

      let winner;
      if (winners.length === 1) {
        winner = winners[0];
      } else if (tiebreakerAnswer && winners.includes(tiebreakerAnswer)) {
        winner = tiebreakerAnswer;
      } else {
        winner = winners[0];
      }

      return this.personas[winner].slug;
    }

    saveResult(personaSlug) {
      trackEvent('quiz_completed', { persona: personaSlug });

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          persona: personaSlug,
          completedAt: new Date().toISOString()
        }));
      } catch (e) {}

      if (this.isLoggedIn) {
        this.saveToCustomerMetafield(personaSlug);
      }
    }

    saveToCustomerMetafield(personaSlug) {
      fetch('/account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'customer[metafields][style.persona]=' + encodeURIComponent(personaSlug)
      }).catch(() => {});
    }

    clearSavedResult() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }

    goToResult(personaSlug) {
      window.location.href = this.resultPageUrl + '?result=' + personaSlug;
    }
  }

  function initQuizzes() {
    document.querySelectorAll('.style-quiz').forEach(section => {
      if (!section.__styleQuizInitialized) {
        section.__styleQuizInitialized = true;
        new StyleQuiz(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  document.addEventListener('shopify:section:load', initQuizzes);
})();
